import type { AudioFilterContext, AudioFilterDefinition, AudioFilterRuntime, PropertyDescriptor } from '../core/definitions';
import type { SettingsBag } from '../core/model';
import type { Registry } from '../core/registry';
import { dbToGain } from './dsp/decibels';
import type { CompressorParams } from './dsp/compressor';
import type { ExpanderParams } from './dsp/expander';
import type { GateParams } from './dsp/gate';
import type { LimiterParams } from './dsp/limiter';
import { numberSetting, smoothTo } from './params';
import { createStereoMatrix } from './stereo-matrix';
import {
  COMPRESSOR_PROCESSOR,
  EXPANDER_PROCESSOR,
  GATE_PROCESSOR,
  LIMITER_PROCESSOR,
  WORKLET_CHANNELS,
  type WorkletMessage,
} from './worklet-protocol';
import { loadWorklets } from './worklets';

type NumberProperty = Extract<PropertyDescriptor, { type: 'number' }>;

/** A numeric setting: its descriptor doubles as the source of the default and the valid range. */
interface NumberSpec {
  key: string;
  label: string;
  fallback: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  description?: string;
}

function read(settings: SettingsBag, spec: NumberSpec): number {
  return numberSetting(settings[spec.key], spec.fallback, spec.min, spec.max);
}

function describe(spec: NumberSpec): NumberProperty {
  const { key, label, min, max, step, unit, description } = spec;
  return { type: 'number', key, label, min, max, step, unit, description, slider: true };
}

function defaultsOf(specs: readonly NumberSpec[]): SettingsBag {
  const bag: SettingsBag = {};
  for (const spec of specs) bag[spec.key] = spec.fallback;
  return bag;
}

// ---------------------------------------------------------------------------
// Worklet-backed dynamics
// ---------------------------------------------------------------------------

function dynamicsFilter<P>(
  kind: string,
  displayName: string,
  processor: string,
  specs: readonly NumberSpec[],
  toParams: (settings: SettingsBag) => P,
): AudioFilterDefinition {
  return {
    type: 'audio',
    kind,
    displayName,
    defaults: () => defaultsOf(specs),
    properties: () => specs.map(describe),
    async create(ctx: AudioFilterContext, settings: SettingsBag): Promise<AudioFilterRuntime> {
      await loadWorklets(ctx.audioContext);
      const node = new AudioWorkletNode(ctx.audioContext, processor, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [WORKLET_CHANNELS],
        channelCount: WORKLET_CHANNELS,
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers',
        processorOptions: { params: toParams(settings) },
      });
      node.onprocessorerror = () => ctx.logger.log('error', 'audio', `${kind} processor failed`);
      const post = (message: WorkletMessage<P>): void => node.port.postMessage(message);
      let destroyed = false;
      return {
        input: node,
        output: node,
        update(next) {
          post({ type: 'params', params: toParams(next) });
        },
        destroy() {
          if (destroyed) return;
          destroyed = true;
          // Lets process() return false so the processor can be collected.
          post({ type: 'dispose' });
          node.onprocessorerror = null;
          node.disconnect();
        },
      };
    },
  };
}

const ATTACK: NumberSpec = { key: 'attackMs', label: 'Attack', fallback: 6, min: 0, max: 500, step: 1, unit: 'ms' };
const RELEASE: NumberSpec = { key: 'releaseMs', label: 'Release', fallback: 60, min: 1, max: 2000, step: 1, unit: 'ms' };
const KNEE: NumberSpec = { key: 'kneeDb', label: 'Knee', fallback: 6, min: 0, max: 24, step: 0.5, unit: 'dB' };

const GATE_OPEN: NumberSpec = { key: 'openThresholdDb', label: 'Open threshold', fallback: -26, min: -96, max: 0, step: 0.5, unit: 'dB' };
const GATE_CLOSE: NumberSpec = {
  key: 'closeThresholdDb',
  label: 'Close threshold',
  fallback: -32,
  min: -96,
  max: 0,
  step: 0.5,
  unit: 'dB',
  description: 'Values above the open threshold are treated as equal to it.',
};
const GATE_ATTACK: NumberSpec = { ...ATTACK, fallback: 25 };
const GATE_HOLD: NumberSpec = { key: 'holdMs', label: 'Hold', fallback: 200, min: 0, max: 3000, step: 1, unit: 'ms' };
const GATE_RELEASE: NumberSpec = { ...RELEASE, fallback: 150 };

const noiseGate = dynamicsFilter<GateParams>(
  'noise-gate',
  'Noise Gate',
  GATE_PROCESSOR,
  [GATE_CLOSE, GATE_OPEN, GATE_ATTACK, GATE_HOLD, GATE_RELEASE],
  (s) => ({
    openThresholdDb: read(s, GATE_OPEN),
    closeThresholdDb: read(s, GATE_CLOSE),
    attackMs: read(s, GATE_ATTACK),
    holdMs: read(s, GATE_HOLD),
    releaseMs: read(s, GATE_RELEASE),
  }),
);

const COMP_THRESHOLD: NumberSpec = { key: 'thresholdDb', label: 'Threshold', fallback: -18, min: -60, max: 0, step: 0.5, unit: 'dB' };
const COMP_RATIO: NumberSpec = { key: 'ratio', label: 'Ratio', fallback: 4, min: 1, max: 32, step: 0.1, unit: ':1' };
const COMP_MAKEUP: NumberSpec = { key: 'makeupDb', label: 'Output gain', fallback: 0, min: -24, max: 24, step: 0.5, unit: 'dB' };

const compressor = dynamicsFilter<CompressorParams>(
  'compressor',
  'Compressor',
  COMPRESSOR_PROCESSOR,
  [COMP_RATIO, COMP_THRESHOLD, KNEE, ATTACK, RELEASE, COMP_MAKEUP],
  (s) => ({
    thresholdDb: read(s, COMP_THRESHOLD),
    ratio: read(s, COMP_RATIO),
    kneeDb: read(s, KNEE),
    attackMs: read(s, ATTACK),
    releaseMs: read(s, RELEASE),
    makeupDb: read(s, COMP_MAKEUP),
  }),
);

const EXP_THRESHOLD: NumberSpec = { key: 'thresholdDb', label: 'Threshold', fallback: -40, min: -96, max: 0, step: 0.5, unit: 'dB' };
const EXP_RATIO: NumberSpec = { key: 'ratio', label: 'Ratio', fallback: 2, min: 1, max: 20, step: 0.1, unit: ':1' };
const EXP_ATTACK: NumberSpec = { ...ATTACK, fallback: 10 };
const EXP_RELEASE: NumberSpec = { ...RELEASE, fallback: 50 };

const expander = dynamicsFilter<ExpanderParams>(
  'expander',
  'Expander',
  EXPANDER_PROCESSOR,
  [EXP_RATIO, EXP_THRESHOLD, KNEE, EXP_ATTACK, EXP_RELEASE],
  (s) => ({
    thresholdDb: read(s, EXP_THRESHOLD),
    ratio: read(s, EXP_RATIO),
    kneeDb: read(s, KNEE),
    attackMs: read(s, EXP_ATTACK),
    releaseMs: read(s, EXP_RELEASE),
  }),
);

const LIMIT_CEILING: NumberSpec = { key: 'ceilingDb', label: 'Ceiling', fallback: -1, min: -60, max: 0, step: 0.1, unit: 'dB' };

const limiter = dynamicsFilter<LimiterParams>(
  'limiter',
  'Limiter',
  LIMITER_PROCESSOR,
  [LIMIT_CEILING, RELEASE],
  (s) => ({ ceilingDb: read(s, LIMIT_CEILING), releaseMs: read(s, RELEASE) }),
);

// ---------------------------------------------------------------------------
// Native-node filters
// ---------------------------------------------------------------------------

const GAIN_DB: NumberSpec = { key: 'gainDb', label: 'Gain', fallback: 0, min: -30, max: 30, step: 0.1, unit: 'dB' };

const gain: AudioFilterDefinition = {
  type: 'audio',
  kind: 'gain',
  displayName: 'Gain',
  defaults: () => defaultsOf([GAIN_DB]),
  properties: () => [describe(GAIN_DB)],
  create(ctx, settings) {
    const node = new GainNode(ctx.audioContext, { gain: dbToGain(read(settings, GAIN_DB)) });
    return {
      input: node,
      output: node,
      update: (next) => smoothTo(ctx.audioContext, node.gain, dbToGain(read(next, GAIN_DB))),
      destroy: () => node.disconnect(),
    };
  },
};

const invertPolarity: AudioFilterDefinition = {
  type: 'audio',
  kind: 'invert-polarity',
  displayName: 'Invert Polarity',
  defaults: () => ({}),
  properties: () => [],
  create(ctx) {
    const node = new GainNode(ctx.audioContext, { gain: -1 });
    return { input: node, output: node, update: () => undefined, destroy: () => node.disconnect() };
  },
};

interface BiquadBand {
  type: BiquadFilterType;
  frequency: number;
  gain: number;
  q: number;
}

/** A series of BiquadFilterNodes whose bands are recomputed from the settings on every update. */
function biquadChain(ctx: AudioFilterContext, initial: readonly BiquadBand[]) {
  const nodes = initial.map(
    (band) => new BiquadFilterNode(ctx.audioContext, { type: band.type, frequency: band.frequency, gain: band.gain, Q: band.q }),
  );
  for (let i = 1; i < nodes.length; i++) nodes[i - 1].connect(nodes[i]);
  return {
    input: nodes[0],
    output: nodes[nodes.length - 1],
    apply(bands: readonly BiquadBand[]): void {
      bands.forEach((band, i) => {
        const node = nodes[i];
        if (node.type !== band.type) node.type = band.type;
        smoothTo(ctx.audioContext, node.frequency, band.frequency);
        smoothTo(ctx.audioContext, node.gain, band.gain);
        smoothTo(ctx.audioContext, node.Q, band.q);
      });
    },
    destroy(): void {
      for (const node of nodes) node.disconnect();
    },
  };
}

function biquadFilter(
  kind: string,
  displayName: string,
  defaults: () => SettingsBag,
  properties: (settings: SettingsBag) => PropertyDescriptor[],
  toBands: (settings: SettingsBag) => BiquadBand[],
): AudioFilterDefinition {
  return {
    type: 'audio',
    kind,
    displayName,
    defaults,
    properties,
    create(ctx, settings) {
      const chain = biquadChain(ctx, toBands(settings));
      return { input: chain.input, output: chain.output, update: (next) => chain.apply(toBands(next)), destroy: chain.destroy };
    },
  };
}

const EQ_BAND_GAIN = { fallback: 0, min: -20, max: 20, step: 0.1, unit: 'dB' };
const EQ3_LOW: NumberSpec = { key: 'lowDb', label: 'Low', description: 'Low shelf at 200 Hz', ...EQ_BAND_GAIN };
const EQ3_MID: NumberSpec = { key: 'midDb', label: 'Mid', description: 'Peak at 1 kHz', ...EQ_BAND_GAIN };
const EQ3_HIGH: NumberSpec = { key: 'highDb', label: 'High', description: 'High shelf at 4 kHz', ...EQ_BAND_GAIN };
const EQ3_LOW_HZ = 200;
const EQ3_MID_HZ = 1000;
const EQ3_MID_Q = 0.7;
const EQ3_HIGH_HZ = 4000;

const eq3 = biquadFilter(
  'eq3',
  '3-Band Equalizer',
  () => defaultsOf([EQ3_LOW, EQ3_MID, EQ3_HIGH]),
  () => [EQ3_HIGH, EQ3_MID, EQ3_LOW].map(describe),
  (s) => [
    { type: 'lowshelf', frequency: EQ3_LOW_HZ, gain: read(s, EQ3_LOW), q: 1 },
    { type: 'peaking', frequency: EQ3_MID_HZ, gain: read(s, EQ3_MID), q: EQ3_MID_Q },
    { type: 'highshelf', frequency: EQ3_HIGH_HZ, gain: read(s, EQ3_HIGH), q: 1 },
  ],
);

// BiquadFilterNode's Q is a resonance in dB for lowpass/highpass; 0 dB is the
// maximally flat (Butterworth) 12 dB/octave response. (Web Audio API, "Filters Characteristics".)
const RESONANCE: NumberSpec = { key: 'resonanceDb', label: 'Resonance', fallback: 0, min: -6, max: 24, step: 0.1, unit: 'dB' };
const HIGHPASS_HZ: NumberSpec = { key: 'frequencyHz', label: 'Cutoff', fallback: 80, min: 20, max: 20000, step: 1, unit: 'Hz' };
const LOWPASS_HZ: NumberSpec = { ...HIGHPASS_HZ, fallback: 12000 };

function passFilter(kind: string, displayName: string, type: 'highpass' | 'lowpass', cutoff: NumberSpec): AudioFilterDefinition {
  return biquadFilter(
    kind,
    displayName,
    () => defaultsOf([cutoff, RESONANCE]),
    () => [cutoff, RESONANCE].map(describe),
    (s) => [{ type, frequency: read(s, cutoff), gain: 0, q: read(s, RESONANCE) }],
  );
}

const highpass = passFilter('highpass', 'High-Pass Filter', 'highpass', HIGHPASS_HZ);
const lowpass = passFilter('lowpass', 'Low-Pass Filter', 'lowpass', LOWPASS_HZ);

const PARAMETRIC_BANDS = 4;
const PARAMETRIC_TYPES = ['off', 'peaking', 'lowshelf', 'highshelf', 'highpass', 'lowpass', 'notch'] as const;
type ParametricType = (typeof PARAMETRIC_TYPES)[number];
const PARAMETRIC_TYPE_LABELS: Record<ParametricType, string> = {
  off: 'Off',
  peaking: 'Peak',
  lowshelf: 'Low shelf',
  highshelf: 'High shelf',
  highpass: 'High-pass',
  lowpass: 'Low-pass',
  notch: 'Notch',
};
const PARAMETRIC_DEFAULT_HZ = [100, 500, 2000, 8000];

interface ParametricSpec {
  typeKey: string;
  frequency: NumberSpec;
  gain: NumberSpec;
  q: NumberSpec;
}

const PARAMETRIC_SPECS: ParametricSpec[] = PARAMETRIC_DEFAULT_HZ.slice(0, PARAMETRIC_BANDS).map((hz, i) => {
  const n = i + 1;
  return {
    typeKey: `band${n}Type`,
    frequency: { key: `band${n}FrequencyHz`, label: `Band ${n} frequency`, fallback: hz, min: 20, max: 20000, step: 1, unit: 'Hz' },
    gain: { key: `band${n}GainDb`, label: `Band ${n} gain`, ...EQ_BAND_GAIN },
    q: { key: `band${n}Q`, label: `Band ${n} Q`, fallback: 1, min: 0.1, max: 18, step: 0.1 },
  };
});

function parametricType(settings: SettingsBag, spec: ParametricSpec): ParametricType {
  const value = settings[spec.typeKey];
  return PARAMETRIC_TYPES.includes(value as ParametricType) ? (value as ParametricType) : 'off';
}

const hasGain = (type: ParametricType): boolean => type === 'peaking' || type === 'lowshelf' || type === 'highshelf';
const hasQ = (type: ParametricType): boolean => type === 'peaking' || type === 'notch';

const eqParametric = biquadFilter(
  'eq-parametric',
  'Parametric Equalizer',
  () => {
    const bag: SettingsBag = {};
    for (const spec of PARAMETRIC_SPECS) {
      bag[spec.typeKey] = 'off';
      Object.assign(bag, defaultsOf([spec.frequency, spec.gain, spec.q]));
    }
    return bag;
  },
  () =>
    PARAMETRIC_SPECS.flatMap((spec, i): PropertyDescriptor[] => [
      {
        type: 'select',
        key: spec.typeKey,
        label: `Band ${i + 1}`,
        options: PARAMETRIC_TYPES.map((value) => ({ value, label: PARAMETRIC_TYPE_LABELS[value] })),
      },
      { ...describe(spec.frequency), visibleWhen: (s) => parametricType(s, spec) !== 'off' },
      { ...describe(spec.gain), visibleWhen: (s) => hasGain(parametricType(s, spec)) },
      { ...describe(spec.q), visibleWhen: (s) => hasQ(parametricType(s, spec)) },
    ]),
  (s) =>
    PARAMETRIC_SPECS.map((spec): BiquadBand => {
      const type = parametricType(s, spec);
      const frequency = read(s, spec.frequency);
      // A peaking band with 0 dB gain is an exact pass-through, so "off" needs no rewiring.
      if (type === 'off') return { type: 'peaking', frequency, gain: 0, q: 1 };
      return {
        type,
        frequency,
        gain: hasGain(type) ? read(s, spec.gain) : 0,
        // Resonance of the pass types is in dB; 0 keeps them maximally flat.
        q: hasQ(type) ? read(s, spec.q) : type === 'highpass' || type === 'lowpass' ? 0 : 1,
      };
    }),
);

const WIDTH: NumberSpec = {
  key: 'width',
  label: 'Width',
  fallback: 100,
  min: 0,
  max: 200,
  step: 1,
  unit: '%',
  description: '0 % is mono, 100 % leaves the signal unchanged, 200 % doubles the side signal.',
};

const stereoWidth: AudioFilterDefinition = {
  type: 'audio',
  kind: 'stereo-width',
  displayName: 'Stereo Width',
  defaults: () => defaultsOf([WIDTH]),
  properties: () => [describe(WIDTH)],
  create(ctx, settings) {
    const matrix = createStereoMatrix(ctx.audioContext);
    // Mid/side scaling: M = (L+R)/2, S = (L-R)/2, out = M ± w·S.
    const apply = (bag: SettingsBag, immediate: boolean): void => {
      const w = read(bag, WIDTH) / 100;
      const direct = (1 + w) / 2;
      const cross = (1 - w) / 2;
      matrix.set(direct, cross, cross, direct, immediate);
    };
    apply(settings, true);
    return { input: matrix.input, output: matrix.output, update: (next) => apply(next, false), destroy: matrix.destroy };
  },
};

const BUILTIN_AUDIO_FILTERS: readonly AudioFilterDefinition[] = [
  gain,
  noiseGate,
  compressor,
  expander,
  limiter,
  eq3,
  eqParametric,
  highpass,
  lowpass,
  invertPolarity,
  stereoWidth,
];

export function registerBuiltinAudioFilters(registry: Registry): void {
  for (const definition of BUILTIN_AUDIO_FILTERS) registry.registerFilter(definition);
}
